import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, Download, Filter, Eye, ChevronLeft, ChevronRight,
  AlertTriangle, CheckCircle, XCircle, FileText, Calendar, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import ViewAuditDialog from './ViewAuditDialog';
import { AnalysisProvenanceBadge } from './AnalysisProvenanceBadge';
import { useTranslation } from 'react-i18next';

const AllAuditsTable = ({ user }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState([]);
  const [totalAudits, setTotalAudits] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [locationFilter, setLocationFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [supervisorFilter, setSupervisorFilter] = useState('');
  
  // For supervisor dropdown
  const [supervisors, setSupervisors] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Selected audit for viewing
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  
  useEffect(() => {
    loadAudits();
    loadFilterOptions();
  }, [page, dateRange, locationFilter, riskFilter, supervisorFilter, searchQuery]);
  
  const loadAudits = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        days: dateRange
      });
      
      if (locationFilter) params.append('location', locationFilter);
      if (riskFilter) params.append('risk_level', riskFilter);
      if (supervisorFilter) params.append('supervisor', supervisorFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axiosInstance.get(`/company/audits/all?${params.toString()}`);
      setAudits(response.data.audits || []);
      setTotalAudits(response.data.total || 0);
    } catch (error) {
      console.error('Error loading audits:', error);
      toast.error('Failed to load audits');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteAudit = async (auditId, auditLocation) => {
    if (!window.confirm(`Are you sure you want to delete this audit?\n\nLocation: ${auditLocation}\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/safety-audits/${auditId}`);
      toast.success('Audit deleted successfully');
      loadAudits(); // Reload the list
    } catch (error) {
      console.error('Failed to delete audit:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete audit');
    }
  };

  const loadFilterOptions = async () => {
    try {
      // Get unique supervisors and locations from audits
      const response = await axiosInstance.get(`/safety-audits?limit=1000`);
      const allAudits = response.data || [];
      
      const uniqueSupervisors = [...new Set(allAudits.map(a => a.supervisorName).filter(Boolean))];
      const uniqueLocations = [...new Set(allAudits.map(a => a.location).filter(Boolean))];
      
      setSupervisors(uniqueSupervisors);
      setLocations(uniqueLocations);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };
  
  const handleExportCSV = async () => {
    try {
      toast.info('Preparing CSV export...');
      
      const params = new URLSearchParams();
      if (dateRange) params.append('days', dateRange);
      if (locationFilter) params.append('location', locationFilter);
      if (riskFilter) params.append('risk_level', riskFilter);
      if (supervisorFilter) params.append('supervisor', supervisorFilter);
      
      const response = await axiosInstance.get(`/company/audits/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audits-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV downloaded successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to export CSV');
    }
  };

  const handleExportAllPDF = async () => {
    try {
      toast.info('Generating PDF report (this may take a moment)...');
      
      const params = new URLSearchParams();
      if (dateRange) params.append('days', dateRange);
      if (locationFilter) params.append('location', locationFilter);
      if (riskFilter) params.append('risk_level', riskFilter);
      if (supervisorFilter) params.append('supervisor', supervisorFilter);
      
      const response = await axiosInstance.get(`/company/audits/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all-audits-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to export PDF');
    }
  };
  
  const getRiskBadge = (audit) => {
    // Official classification comes from the same calculation as the PDF reports.
    const level = audit.calculatedScoring?.risk_level;
    if (!level) return <Badge variant="outline">N/A</Badge>;

    if (level === 'CRITICAL') {
      return <Badge className="bg-red-700"><AlertTriangle className="w-3 h-3 mr-1" />{t('auditsTable.criticalRisk')}</Badge>;
    } else if (level === 'HIGH') {
      return <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />{t('auditsTable.highRisk')}</Badge>;
    } else if (level === 'MODERATE') {
      return <Badge className="bg-orange-500"><AlertTriangle className="w-3 h-3 mr-1" />{t('auditsTable.mediumRisk')}</Badge>;
    } else {
      return <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />{t('auditsTable.lowRisk')}</Badge>;
    }
  };
  
  const getComplianceScore = (audit) => {
    // Single source of truth: the same calculation the PDF reports use.
    const scoring = audit.calculatedScoring;
    if (!scoring || !scoring.applicable_items) return 'N/A';
    return `${scoring.compliance_rate}%`;
  };
  
  const getComplianceBadge = (audit) => {
    const score = getComplianceScore(audit);
    if (score === 'N/A') return <Badge variant="outline">N/A</Badge>;
    
    const numScore = parseInt(score);
    if (numScore >= 80) {
      return <Badge className="bg-green-600">{score}</Badge>;
    } else if (numScore >= 60) {
      return <Badge className="bg-orange-500">{score}</Badge>;
    } else {
      return <Badge className="bg-red-600">{score}</Badge>;
    }
  };
  
  const totalPages = Math.ceil(totalAudits / limit);
  
  const handleReset = () => {
    setSearchQuery('');
    setDateRange('30');
    setLocationFilter('');
    setRiskFilter('');
    setSupervisorFilter('');
    setPage(1);
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('auditsTable.allSafetyAudits')}</h2>
          <p className="text-gray-600 mt-1">
            {totalAudits} total audit{totalAudits !== 1 ? 's' : ''} • Page {page} of {totalPages}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportCSV} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            {t('auditsTable.exportCsv')}
          </Button>
          <Button onClick={handleExportAllPDF} className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t('auditsTable.exportAllPdf')}
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            {t('auditsTable.filtersSearch')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <Label>{t('auditsTable.searchLabel')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('auditsTable.searchAudits')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Date Range */}
            <div>
              <Label>{t('auditsTable.dateRange')}</Label>
              <Select value={dateRange} onValueChange={(val) => { setDateRange(val); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">{t('auditsTable.last7Days')}</SelectItem>
                  <SelectItem value="30">{t('auditsTable.last30Days')}</SelectItem>
                  <SelectItem value="90">{t('auditsTable.last90Days')}</SelectItem>
                  <SelectItem value="180">{t('auditsTable.last6Months')}</SelectItem>
                  <SelectItem value="365">{t('auditsTable.lastYear')}</SelectItem>
                  <SelectItem value="all">{t('auditsTable.allTime')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Location */}
            <div>
              <Label>{t('auditsTable.locationLabel')}</Label>
              <Select value={locationFilter || "all"} onValueChange={(val) => { setLocationFilter(val === "all" ? "" : val); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder={t('auditsTable.allLocations')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Risk Level */}
            <div>
              <Label>{t('auditsTable.riskLevel')}</Label>
              <Select value={riskFilter || "all"} onValueChange={(val) => { setRiskFilter(val === "all" ? "" : val); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder={t('auditsTable.allRiskLevels')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auditsTable.allRiskLevels')}</SelectItem>
                  <SelectItem value="High">{t('auditsTable.highRisk')}</SelectItem>
                  <SelectItem value="Medium">{t('auditsTable.mediumRisk')}</SelectItem>
                  <SelectItem value="Low">{t('auditsTable.lowRisk')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Supervisor */}
            <div>
              <Label>{t('auditsTable.supervisorLabel')}</Label>
              <Select value={supervisorFilter || "all"} onValueChange={(val) => { setSupervisorFilter(val === "all" ? "" : val); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder={t('auditsTable.allSupervisors')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auditsTable.allSupervisors')}</SelectItem>
                  {supervisors.map(sup => (
                    <SelectItem key={sup} value={sup}>{sup}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Reset Button */}
            <div className="flex items-end">
              <Button variant="outline" onClick={handleReset} className="w-full">
                {t('auditsTable.resetFilters')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : audits.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-semibold">{t('auditsTable.noAuditsFound')}</p>
              <p className="text-sm mt-2">{t('auditsTable.tryAdjustingFilters')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auditsTable.dateColumn')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auditsTable.locationColumn')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auditsTable.projectColumn')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auditsTable.supervisorColumn')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auditsTable.complianceColumn')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auditsTable.riskLevelColumn')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auditsTable.analysisOriginColumn')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auditsTable.actionsColumn')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {audits.map((audit) => (
                    <tr key={audit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          {new Date(audit.auditDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {audit.location || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-testid={`audit-project-cell-${audit.id}`}>
                        {audit.projectName || <span className="text-gray-400">{t('auditsTable.unassignedProject')}</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {audit.supervisorName || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getComplianceBadge(audit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getRiskBadge(audit)}
                      </td>
                      <td className="px-6 py-4 text-sm" data-testid={`audit-provenance-cell-${audit.id}`}>
                        {audit.aiAnalysisProvenanceBadge?.hasAnalysis
                          ? <AnalysisProvenanceBadge badge={audit.aiAnalysisProvenanceBadge} compact />
                          : <span className="text-gray-400 text-xs">{t('auditsTable.noAnalysisYet')}</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`audit-view-button-${audit.id}`}
                            onClick={() => {
                              setSelectedAudit(audit);
                              setShowAuditDialog(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {t('auditsTable.viewButton')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:border-red-600"
                            data-testid={`audit-delete-button-${audit.id}`}
                            onClick={() => handleDeleteAudit(audit.id, audit.location)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalAudits)} of {totalAudits} audits
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {t('common.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              {t('common.next')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Audit View Dialog */}
      <ViewAuditDialog
        user={user}
        audit={selectedAudit}
        open={showAuditDialog}
        onClose={() => {
          setShowAuditDialog(false);
          setSelectedAudit(null);
        }}
        onDelete={loadAudits}
        onUpdate={loadAudits}
      />
    </div>
  );
};

export default AllAuditsTable;
